import { useEffect, useState } from "react";
import {
  builtinFieldPresets,
  effectiveFieldPresets,
  applyFieldPreset,
  validationRules,
  validationSettingsSchema,
  formatSettingsSchema,
  safePattern,
  newField,
  formatValue,
  policyValueError,
  policyFitsType,
  formatKindsFor,
  browserLocale,
  resolveFormatLocale,
  formattingLocales,
  patternError,
  validationMessage,
  applicableValidation,
  numericValue,
  strictDate,
  strictDateTime,
  strictTime,
  supportedDateLocales,
  type Field,
  type Project,
  type FieldPreset,
  type FieldPresetCatalog,
  type FieldPresetScope,
  type FieldPresetWrite,
} from "@proof/core";
import { useAuthoringServices } from "./services";
import { ValidationSettingsFields } from "./ValidationSettingsFields";
import { Select,
  Button,
  Control,
  Modal,
  SearchableSelect,
  Toggle,
  type SelectOption,
} from "./components";
/** Locale choices, led by the viewer's browser locale. */
function localeOptions(dateOnly: boolean): SelectOption[] {
  let names: Intl.DisplayNames | undefined;
  try {
    names = new Intl.DisplayNames(undefined, { type: "language" });
  } catch {
    names = undefined;
  }
  const tags = (dateOnly ? supportedDateLocales : formattingLocales).filter(
    (tag) => {
      try {
        return Intl.NumberFormat.supportedLocalesOf([tag]).length > 0;
      } catch {
        return false;
      }
    },
  );
  return [
    {
      value: browserLocale,
      label: "User's Browser",
      detail: `Currently ${resolveFormatLocale(browserLocale)}`,
    },
    ...tags.map((tag) => ({
      value: tag,
      label: names?.of(tag) || tag,
      detail: tag,
    })),
  ];
}
type Kind = "validation" | "format";
type Settings = Field["validation"] | Field["format"];
function parse(kind: Kind, value: Settings) {
  const result = (
    kind === "validation" ? validationSettingsSchema : formatSettingsSchema
  ).parse(value);
  if (kind === "validation" && "pattern" in result && result.pattern)
    safePattern(result.pattern);
  return result;
}
/** Mirrors the engine's checks for one sample value in the editor preview. */
function sampleValidationError(f: Field, sample: string) {
  const v = f.validation;
  if (f.type === "checkbox") {
    const count = sample.split(";").filter(Boolean).length;
    if (v.minSelected != null && count < v.minSelected)
      return validationMessage(v, "minSelected", `select at least ${v.minSelected}`);
    if (v.maxSelected != null && count > v.maxSelected)
      return validationMessage(v, "maxSelected", `select no more than ${v.maxSelected}`);
    return;
  }
  if (v.minLength != null && sample.length < v.minLength)
    return validationMessage(v, "minLength", `minimum ${v.minLength} characters`, sample);
  if (v.maxLength != null && sample.length > v.maxLength)
    return validationMessage(v, "maxLength", `maximum ${v.maxLength} characters`, sample);
  const pattern = patternError(v, sample);
  if (pattern) return pattern;
  if (f.type === "number") {
    const n = numericValue(sample);
    if (Number.isFinite(n) && v.min != null && n < v.min)
      return validationMessage(v, "min", `minimum ${v.min}`, sample);
    if (Number.isFinite(n) && v.max != null && n > v.max)
      return validationMessage(v, "max", `maximum ${v.max}`, sample);
  }
  const rule = policyValueError(f, sample);
  return rule ? validationMessage(v, "rule", rule, sample) : undefined;
}
/** A local draft keeps partial/invalid patterns out of the document until Apply. */
export function PolicySettingsEditor({
  kind,
  value,
  onApply,
  onPendingChange,
  fieldType,
}: {
  kind: Kind;
  fieldType?: Field["type"];
  value: Settings;
  onApply(value: Settings): void;
  onPendingChange?(pending: boolean): void;
}) {
  const [valueType, setValueType] = useState<Field["type"]>(
    fieldType || "text",
  );
  const type = fieldType || valueType;
  const [draft, setDraft] = useState<Record<string, any>>(value),
    [sample, setSample] = useState(
      kind === "format" ? "1234.567" : "https://example.com",
    );
  useEffect(() => {
    setDraft(value);
  }, [value]);
  useEffect(() => {
    onPendingChange?.(JSON.stringify(draft) !== JSON.stringify(value));
  }, [draft, value, onPendingChange]);
  const set = (key: string, value: unknown) =>
    setDraft({ ...draft, [key]: value, preset: undefined });
  const applicable = (value: Record<string, any>) =>
    kind === "validation"
      ? applicableValidation(type, value as Field["validation"])
      : value;
  let error = "";
  try {
    parse(kind, applicable(draft));
  } catch (e) {
    error =
      "issues" in (e as object)
        ? (e as { issues: { message: string }[] }).issues
            .map((issue) => issue.message)
            .join("; ")
        : (e as Error).message;
  }
  let preview = "";
  if (!error) {
    const f = { ...newField(type), [kind]: parse(kind, applicable(draft)) };
    preview =
      kind === "format"
        ? formatValue(f, sample)
        : sampleValidationError(f, sample) || "Valid value";
    if (
      kind === "format" &&
      draft.kind === "date" &&
      !strictDate(sample) &&
      !strictDateTime(sample) &&
      !strictTime(sample)
    )
      preview = "Enter an ISO date, date/time, or time to preview.";
  }
  return (
    <div className="policy-settings-editor">
      {!fieldType && (
        <Control label="Input value type">
          <Select
            value={valueType}
            onChange={(event) =>
              setValueType(event.target.value as Field["type"])
            }
          >
            <option value="text">Text</option>
            <option value="number">Number</option>
            <option value="checkbox">Checkbox group</option>
          </Select>
        </Control>
      )}
      {kind === "validation" ? (
        <ValidationSettingsFields type={type} draft={draft} set={set} />
      ) : (
        <>
          <Control label="Output formatting">
            <Select
              value={draft.kind || "raw"}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  preset: undefined,
                  kind: e.target.value,
                  ...(e.target.value === "date"
                    ? {
                        datePattern: draft.datePattern || "yyyy-MM-dd",
                        locale:
                          !draft.locale ||
                          draft.locale === browserLocale ||
                          supportedDateLocales.includes(draft.locale)
                            ? draft.locale || browserLocale
                            : browserLocale,
                      }
                    : {}),
                  ...(e.target.value === "number"
                    ? {
                        numberPattern: draft.numberPattern || "#,##0.00",
                        currency: draft.currency || "USD",
                      }
                    : {}),
                })
              }
            >
              {formatKindsFor(type).map((option) => (
                <option key={option} value={option}>
                  {
                    {
                      raw: "Original value",
                      text: "Text",
                      date: "Date / time",
                      number: "Number",
                    }[option || "raw"]
                  }
                </option>
              ))}
            </Select>
          </Control>
          {draft.kind === "date" && (
            <>
              <Control label="Date / time style">
                <Select
                  value={
                    [
                      "yyyy-MM-dd",
                      "MM/dd/yyyy",
                      "dd/MM/yyyy",
                      "MMM d, yyyy",
                      "MMMM d, yyyy",
                      "yyyy-MM-dd HH:mm",
                      "HH:mm",
                      "HH:mm:ss",
                      "h:mm a",
                    ].includes(draft.datePattern)
                      ? draft.datePattern
                      : "custom"
                  }
                  onChange={(e) =>
                    e.target.value !== "custom"
                      ? set("datePattern", e.target.value)
                      : set("datePattern", "")
                  }
                >
                  <option value="custom">Custom pattern</option>
                  {[
                    "yyyy-MM-dd",
                    "MM/dd/yyyy",
                    "dd/MM/yyyy",
                    "MMM d, yyyy",
                    "MMMM d, yyyy",
                    "yyyy-MM-dd HH:mm",
                    "HH:mm",
                    "HH:mm:ss",
                    "h:mm a",
                  ].map((p) => (
                    <option key={p}>{p}</option>
                  ))}
                </Select>
              </Control>
              <Control
                label="Date / time pattern"
                note="Unicode tokens: yyyy year, MM month, dd day, HH hours, mm minutes. Quote literal text. Values use UTC."
              >
                <input
                  value={draft.datePattern || ""}
                  onChange={(e) => set("datePattern", e.target.value)}
                />
              </Control>
            </>
          )}
          {draft.kind === "number" && (
            <>
              <Control label="Number style">
                <Select
                  value={
                    [
                      "#,##0",
                      "#,##0.00",
                      "#,##0.####",
                      "0.0%",
                      "000000",
                      "¤#,##0.00",
                    ].includes(draft.numberPattern)
                      ? draft.numberPattern
                      : "custom"
                  }
                  onChange={(e) =>
                    set(
                      "numberPattern",
                      e.target.value === "custom" ? "" : e.target.value,
                    )
                  }
                >
                  <option value="custom">Custom pattern</option>
                  {[
                    "#,##0",
                    "#,##0.00",
                    "#,##0.####",
                    "0.0%",
                    "000000",
                    "¤#,##0.00",
                  ].map((p) => (
                    <option key={p}>{p}</option>
                  ))}
                </Select>
              </Control>
              <Control
                label="Number pattern"
                note="0 required digit; # optional digit; comma groups; % percent; ¤ currency."
              >
                <input
                  value={draft.numberPattern || ""}
                  onChange={(e) => set("numberPattern", e.target.value)}
                />
              </Control>
              {draft.numberPattern?.includes("¤") && (
                <Control label="Currency code">
                  <input
                    maxLength={3}
                    value={draft.currency || "USD"}
                    onChange={(e) =>
                      set("currency", e.target.value.toUpperCase())
                    }
                  />
                </Control>
              )}
            </>
          )}
          <Control
            label="Formatting locale"
            help="Controls separators, currency symbols, month names and casing. User's Browser formats with each viewer's own browser language."
            helpDocs="/help?guide=field-policies"
          >
            <SearchableSelect
              label="Formatting locale"
              value={draft.locale || browserLocale}
              onChange={(value) => set("locale", value)}
              options={localeOptions(draft.kind === "date")}
              searchPlaceholder="Search locales…"
            />
          </Control>
          <div className="control-grid">
            {["prefix", "suffix"].map((k) => (
              <Control key={k} label={k === "prefix" ? "Prefix" : "Suffix"}>
                <input
                  value={draft[k] || ""}
                  onChange={(e) => set(k, e.target.value)}
                />
              </Control>
            ))}
          </div>
          <Control label="Text case">
            <Select
              value={draft.uppercase ? "upper" : draft.casing || "unchanged"}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  preset: undefined,
                  uppercase: e.target.value === "upper",
                  casing:
                    e.target.value === "upper" ? "unchanged" : e.target.value,
                })
              }
            >
              <option value="unchanged">Keep case</option>
              <option value="upper">UPPERCASE</option>
              <option value="lowercase">lowercase</option>
              <option value="title">Title Case</option>
            </Select>
          </Control>
          <Toggle
            label="Trim spaces"
            value={!!draft.trim}
            onChange={(v) => set("trim", v)}
          />
        </>
      )}
      <Control label="Test value">
        <input value={sample} onChange={(e) => setSample(e.target.value)} />
      </Control>
      {error ? (
        <p role="alert">{error}</p>
      ) : (
        <p aria-label="Policy preview">{preview}</p>
      )}
      <Button
        disabled={!!error}
        onClick={() => onApply(parse(kind, applicable(draft)))}
      >
        Apply {kind === "format" ? "formatting" : "validation"} settings
      </Button>
    </div>
  );
}
function useCatalog(projectId?: string, fieldId?: string, revision = 0) {
  const services = useAuthoringServices(),
    [catalog, setCatalog] = useState<FieldPresetCatalog>({
      context: {
        siteId: "",
        companyId: "",
        canManageSite: false,
        canManageCompany: false,
        canManageProject: false,
      },
      presets: builtinFieldPresets(),
    }),
    [error, setError] = useState(""),
    [reload, setReload] = useState(0);
  useEffect(() => {
    let active = true;
    setError("");
    setCatalog({
      context: {
        siteId: "",
        companyId: "",
        canManageSite: false,
        canManageCompany: false,
        canManageProject: false,
      },
      presets: builtinFieldPresets(),
    });
    if (!services.listFieldPresets) {
      setCatalog({
        context: {
          siteId: "",
          companyId: "",
          canManageSite: false,
          canManageCompany: false,
          canManageProject: false,
        },
        presets: builtinFieldPresets(),
      });
      return;
    }
    services
      .listFieldPresets({
        ...(projectId ? { projectId } : {}),
        ...(fieldId ? { fieldId } : {}),
      })
      .then((c) => {
        if (active) setCatalog(c);
      })
      .catch((e) => {
        if (active) {
          setError(e.message);
          setCatalog((c) => ({ ...c, presets: builtinFieldPresets() }));
        }
      });
    return () => {
      active = false;
    };
  }, [services.listFieldPresets, projectId, fieldId, reload, revision]);
  return { catalog, error, refresh: () => setReload((n) => n + 1) };
}
export function FieldPolicyControls({
  project,
  field,
  onChange,
  sharedOnly = false,
  kinds = ["validation", "format"],
}: {
  project: Project;
  field: Field;
  onChange(patch: Partial<Field>): void;
  sharedOnly?: boolean;
  /** Render only these policy kinds (each has its own section). */
  kinds?: Kind[];
}) {
  const { editorPresentation } = useAuthoringServices();
  const [revision, setRevision] = useState(0);
  const { catalog, error } = useCatalog(
      sharedOnly ? undefined : project.id,
      sharedOnly ? undefined : field.id,
      revision,
    ),
    [saving, setSaving] = useState<Kind>();
  return (
    <>
      {error && (
        <p role="alert">
          Shared presets unavailable: {error}. Standard presets remain
          available.
        </p>
      )}
      {kinds
        .filter((kind) =>
          kind === "validation"
            ? editorPresentation?.enableValidation !== false
            : editorPresentation?.enableFormatting !== false,
        )
        .map((kind) => {
          const selected = field[kind].preset,
            rows = effectiveFieldPresets(catalog.presets).filter(
              (p) =>
                p.kind === kind &&
                policyFitsType(
                  kind,
                  field.type,
                  (p.value || {}) as Record<string, unknown>,
                ),
            );
          return (
            <section
              key={kind}
              aria-label={
                kind === "format" ? "Formatting policy" : "Validation policy"
              }
            >
              <Control
                label={
                  kind === "format" ? "Formatting preset" : "Validation preset"
                }
                help={
                  kind === "format"
                    ? "Start from a saved formatting preset or keep custom settings. Only presets that fit this control type are listed."
                    : "Start from a saved validation preset or keep custom settings. Only presets that fit this control type are listed."
                }
                helpDocs="/help?guide=field-policies"
              >
                <Select
                  value={
                    selected ? `${selected.id}:${selected.version}` : "custom"
                  }
                  onChange={(e) => {
                    const preset = rows.find(
                      (p) => `${p.id}:${p.version}` === e.target.value,
                    );
                    if (preset)
                      onChange({
                        [kind]: applyFieldPreset(field, preset)[kind],
                      });
                  }}
                >
                  <option value="custom">Custom variable settings</option>
                  {selected &&
                    !rows.some(
                      (p) =>
                        p.id === selected.id && p.version === selected.version,
                    ) && (
                      <option value={`${selected.id}:${selected.version}`}>
                        {selected.name} · pinned v{selected.version}
                      </option>
                    )}
                  {rows.map((p) => (
                    <option key={p.id} value={`${p.id}:${p.version}`}>
                      {p.name}
                      {p.scope.kind === "application"
                        ? ""
                        : ` (${p.scope.kind})`}
                    </option>
                  ))}
                </Select>
              </Control>
              <PolicySettingsEditor
                key={`${field.id}:${kind}`}
                kind={kind}
                fieldType={field.type}
                value={field[kind]}
                onApply={(value) => onChange({ [kind]: value })}
              />
              <Button onClick={() => setSaving(kind)}>
                Save {kind === "format" ? "formatting" : "validation"} as
                reusable preset
              </Button>
            </section>
          );
        })}
      {saving && (
        <Modal
          title="Save reusable preset"
          wide
          onClose={() => {
            setSaving(undefined);
            setRevision((n) => n + 1);
          }}
        >
          <FieldPresetLibrary
            project={sharedOnly ? undefined : project}
            fieldId={sharedOnly ? undefined : field.id}
            kind={saving}
            initialValue={field[saving]}
          />
        </Modal>
      )}
    </>
  );
}
export function FieldPresetLibrary({
  project,
  kind,
  fieldId: initialField,
  initialValue,
}: {
  project?: Project;
  kind: Kind;
  fieldId?: string;
  initialValue?: Settings;
}) {
  const services = useAuthoringServices(),
    [fieldId, setFieldId] = useState(initialField || ""),
    { catalog, error, refresh } = useCatalog(project?.id, fieldId || undefined),
    [editing, setEditing] = useState<FieldPreset>(),
    [name, setName] = useState(""),
    [key, setKey] = useState(""),
    [scope, setScope] = useState<FieldPresetScope["kind"]>(
      project ? "project" : "company",
    ),
    [value, setValue] = useState<Settings>(
      initialValue || (kind === "format" ? formatSettingsSchema.parse({}) : {}),
    ),
    [notice, setNotice] = useState(""),
    [busy, setBusy] = useState(false),
    [pending, setPending] = useState(false);
  const context = catalog.context,
    rows = catalog.presets.filter((p) => p.kind === kind),
    canSave =
      !!services.saveFieldPreset &&
      !error &&
      (scope === "site"
        ? context.canManageSite
        : scope === "company"
          ? context.canManageCompany
          : context.canManageProject &&
            !!project &&
            (scope !== "variable" || !!fieldId));
  const save = async (archived = false) => {
    setBusy(true);
    setNotice("");
    try {
      const s =
        editing?.scope ||
        ({
          kind: scope,
          siteId: context.siteId,
          ...(scope !== "site" ? { companyId: context.companyId } : {}),
          ...(["project", "variable"].includes(scope)
            ? { projectId: project!.id }
            : {}),
          ...(scope === "variable" ? { fieldId } : {}),
        } as FieldPresetScope);
      const row = await services.saveFieldPreset!({
        ...(editing ? { id: editing.id } : {}),
        kind,
        key,
        name,
        scope: s,
        value,
        expectedVersion: editing?.version || 0,
        ...(archived ? { archived: true } : {}),
      });
      setEditing(row);
      refresh();
      setNotice(
        archived
          ? "Preset archived. Existing variables keep their pinned settings."
          : `Saved ${row.name} · version ${row.version}`,
      );
    } catch (e) {
      setNotice((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="field-preset-library">
      <p>
        Standard presets are available application wide. Custom presets can
        override the same key for a site, company, project or variable.
        Variables copy the selected version.
      </p>
      {error && <p role="alert">{error}</p>}
      {project && (
        <Control label="Preset variable scope">
          <Select
            value={fieldId}
            onChange={(e) => {
              setFieldId(e.target.value);
              setEditing(undefined);
            }}
          >
            <option value="">All project variables</option>
            {project.fields.map((f) => (
              <option key={f.id} value={f.id}>
                {f.label}
              </option>
            ))}
          </Select>
        </Control>
      )}
      <div className="control-grid">
        <section aria-label="Available presets">
          {rows.map((p) => (
            <div key={p.id} className="resource-row">
              <strong>{p.name}</strong>
              <small>
                {" "}
                {p.scope.kind} · v{p.version}
                {p.archived ? " · archived" : ""}
              </small>{" "}
              <Button
                onClick={() => {
                  setEditing(undefined);
                  setName(p.name + " copy");
                  setKey(p.key);
                  setValue(parse(kind, p.value as Settings));
                  setNotice("");
                }}
              >
                Use as starting point
              </Button>
              {p.scope.kind !== "application" && (
                <Button
                  onClick={() => {
                    setEditing(p);
                    setName(p.name);
                    setKey(p.key);
                    setScope(p.scope.kind);
                    setValue(parse(kind, p.value as Settings));
                    setNotice("");
                  }}
                >
                  Edit preset
                </Button>
              )}
            </div>
          ))}
        </section>
        <section aria-label="Preset editor">
          <h3>{editing ? "Edit preset version" : "Create scoped preset"}</h3>
          <Button
            onClick={() => {
              setEditing(undefined);
              setName("");
              setKey("");
              setNotice("");
            }}
          >
            New preset
          </Button>
          <Control label="Preset name">
            <input value={name} onChange={(e) => setName(e.target.value)} />
          </Control>
          <Control
            label="Preset key"
            note="Use the same key as a broader preset to override it in this scope."
          >
            <input
              disabled={!!editing}
              value={key}
              onChange={(e) => setKey(e.target.value)}
            />
          </Control>
          <Control label="Preset scope">
            <Select
              disabled={!!editing}
              value={scope}
              onChange={(e) => setScope(e.target.value as typeof scope)}
            >
              <option value="site" disabled={!context.canManageSite}>
                Site · {context.siteName || context.siteId}
              </option>
              <option value="company" disabled={!context.canManageCompany}>
                Company · {context.companyName || context.companyId}
              </option>
              <option
                value="project"
                disabled={!project || !context.canManageProject}
              >
                Project · {project?.name || "open a project"}
              </option>
              <option
                value="variable"
                disabled={!project || !fieldId || !context.canManageProject}
              >
                Variable ·{" "}
                {project?.fields.find((f) => f.id === fieldId)?.label ||
                  "select a variable"}
              </option>
            </Select>
          </Control>
          <PolicySettingsEditor
            kind={kind}
            value={value}
            onPendingChange={setPending}
            onApply={(v) => {
              setValue(v);
              setNotice(
                "Settings applied to draft. Save the preset version to store it.",
              );
            }}
          />
          <p>Use Apply settings above before saving the preset.</p>
          <div className="button-row">
            <Button
              disabled={
                busy || pending || !canSave || !name.trim() || !key.trim()
              }
              onClick={() => void save()}
            >
              Save preset version
            </Button>
            {editing && !editing.archived && (
              <Button
                disabled={busy || pending || !canSave}
                onClick={() => void save(true)}
              >
                Archive preset
              </Button>
            )}
          </div>
          {notice && <p role="status">{notice}</p>}
        </section>
      </div>
    </div>
  );
}
